/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { CollectionResponse } from './../models/collectionResponse.model';
import { ApiDependenciesService } from './../../api/services/api-dependencies.service';
import { ApiEntity } from '../../api/models/api-entity.model';
import { BaseApiService } from '../../api/services/base-api.service';
import { Base64Image } from 'app/shared/models/base64-image.model';
import { ExtendedBlob } from 'app/shared/models/extended-blob';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { ApiResponseSummary } from '../models/api-info.model';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { BlockUiService } from 'app/layout/components/block-ui/block-ui.service';
import { formatAsString } from '../helpers/date-helpers';

export abstract class DataService<T extends ApiEntity> extends BaseApiService {
  @BlockUI() blockUI: NgBlockUI;
  useBlockUi = true;
  blockUIContext = 'block-ui-context unknown';

  constructor(protected dependencies: ApiDependenciesService, protected blockerService: BlockUiService = null) {
    super(dependencies);
  }

  //#region Block UI
  stopBlockUI() {
    if (this.blockerService) {  
      this.blockerService.stopBlockUI(this.blockUIContext);
    } else {
      if (this.useBlockUi) {
        this.blockUI.stop();
      }
    }
  }

  startBlockUI() {
    if (this.blockerService) {
      this.blockerService.startBlockUI(this.blockUIContext);
    } else {
      if (this.useBlockUi) {
        this.blockUI.start();
      }
    }
  }
  //#endregion

  public async queryCollection(queryParams: any = {}): Promise<CollectionResponse<T>> {
    let result: CollectionResponse<T> = null;
    const request = this.dependencies.http.post(this.buildUrl(), this.normalizeRequestPayload(queryParams), { headers: this.buildHeaders() }).toPromise();

    await request
      .then((response: Response) => (result = this.parseDataQueryCollectionResponse(response)))
      .catch((error) => {
        this.handleApiError(error, request);
      });

    return Promise.resolve(result);
  }

  public async getCollection(): Promise<T[]> {
    let result: T[] = null;
    const request = this.dependencies.http.get(this.buildUrl(), { headers: this.buildHeaders() }).toPromise();

    await request
      .then((response: ApiResponseSummary<T>) => (result = this.parseCollectionResponse(response)))
      .catch((error) => this.handleApiError(error, request));

    return Promise.resolve(result);
  }

  public async getSingle(): Promise<T> {
    let result: T = null;
    const url = this.buildUrl();
    const headers = this.buildHeaders();
    const request = this.dependencies.http.get(url, { headers: headers }).toPromise();

    await request
      .then((response: Response) => {
        result = this.parseSingleResponse(response);
      })
      .catch((error) => {
        this.handleApiError(error, request);
      });

    return Promise.resolve(result);
  }

  public async getSingleNoThrow(): Promise<T> {
    let result: T = null;
    const url = this.buildUrl();
    const headers = this.buildHeaders();
    // TODO(WH): catch any errors from OPTIONS request
    const request = this.dependencies.http.get(url, { headers: headers }).toPromise();

    await request
      .then((response: Response) => {
        result = this.parseSingleResponse(response);
      })
      .catch((error) => {
        result = null;
      });

    return Promise.resolve(result);
  }

  public async getFileAsBase64(): Promise<Base64Image> {
    const request: Promise<Object> = this.dependencies.http
      .get(this.buildUrl(), { headers: this.buildHeaders(), responseType: 'blob' })
      .toPromise();

    return request.then((response: Response) => {
      return new Promise((resolve, reject) => {
        if (response.status === 204) {
          resolve(null);
        }
        const fileReader = new FileReader();
        let fileBlob;
        Promise.resolve(response.blob().then((myBlob) => (fileBlob = myBlob)));

        let blob = new Blob([fileBlob]);
        fileReader.readAsDataURL(blob);
        fileReader.onloadend = () => {
          const image = new Image();
          image.onload = () => {
            // TODO: [Technical Debt][dpb][2021-04-22] Fix for Angular 11 upgrade
            //resolve({ base64: fileReader.result, width: image.width, height: image.height });
          };
        };
      });
    });
    // TODO: [Technical Debt][dpb][2021-04-22] Fix for Angular 11 upgrade
    //.catch((error) => this.handleApiError(error, request));
  }

  public async getFileAsBlobOld(): Promise<Blob> {
    const result: Blob = null;
    const request: Promise<Object> = this.dependencies.http
      .get(this.buildUrl(), { headers: this.buildHeaders(), responseType: 'blob' })
      .toPromise();

    return request.then((response: Response) => {
      return new Promise((resolve) => {
        if (response.status === 204) {
          resolve(null);
        }
        resolve(response.blob());
      });
    });
    // TODO: [Technical Debt][dpb][2021-04-22] Fix for Angular 11 upgrade
    //.catch((error) => this.handleApiError(error, request));
  }

  public async getFileAsBlob(postData: any): Promise<ExtendedBlob> {
    const result: ExtendedBlob = new ExtendedBlob();
    const request: Promise<Object> = this.dependencies.http
      .get(this.buildUrl(), { headers: this.buildHeaders(), responseType: 'blob', observe: 'response' as 'body' })
      .toPromise();

    return request.then((response: any) => {
      result.blob = response.body;

      result.metaData = this.getFileNameFromHttpResponse(response);
      return new Promise((resolve, reject) => {
        if (response.status === 204) {
          resolve(null);
        }
        resolve(result);
      });
    });
    // TODO: [Technical Debt][dpb][2021-04-22] Fix for Angular 11 upgrade
    //.catch((error) => this.handleApiError(error, request));
  }

  public async getFileAsExtendedBlob(): Promise<ExtendedBlob> {
    const result: ExtendedBlob = new ExtendedBlob();
    const request: Promise<Object> = this.dependencies.http
      .get(this.buildUrl(), { headers: this.buildHeaders(), responseType: 'blob' })
      .toPromise();

    return request.then((response: Response) => {
      result.metaData = this.getFileNameFromHttpResponse(response);
      Promise.resolve(response.blob().then((blob) => (result.blob = blob)));
      return new Promise((resolve, reject) => {
        if (response.status === 204) {
          resolve(null);
        }
        resolve(result);
      });
    });
    // TODO: [Technical Debt][dpb][2021-04-22] Fix for Angular 11 upgrade
    //.catch((error) => this.handleApiError(error, request));
  }

  private getFileNameFromHttpResponse(httpResponse) {
    var contentDispositionHeader = httpResponse.headers.get('Content-Disposition');
    var result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
    return result.replace(/"/g, '');
  }

  // todo: rename this with better name get/post
  public async getFileAsBlobWithPost(postData: any): Promise<ExtendedBlob> {
    const result: ExtendedBlob = new ExtendedBlob();
    const request: Promise<Object> = this.dependencies.http
      .post(this.buildUrl(), this.normalizeRequestPayload(postData), { headers: this.buildHeaders(), responseType: 'blob', observe: 'response' as 'body' })
      .toPromise();

    return request.then((response: any) => {
      result.blob = response.body;

      result.metaData = this.getFileNameFromHttpResponse(response);
      return new Promise((resolve, reject) => {
        if (response.status === 204) {
          resolve(null);
        }
        resolve(result);
      });
    });
    // TODO: [Technical Debt][dpb][2021-04-22] Fix for Angular 11 upgrade
    //.catch((error) => this.handleApiError(error, request));
  }

  public async sendFileAsBlob(arrayBuffer: ArrayBuffer, fileExtension: string): Promise<boolean> {
    const url = this.buildUrl();
    const headers = this.buildHeaders();
    const ok = await this.upload(url, headers, arrayBuffer, fileExtension);
    return Promise.resolve(ok);
  }

  // TODO: [Review][DPB][2019-08-21] This looks like a hangove from Watson homes. Can it be resused or should it be scrapped?
  private upload(url: string, headers: HttpHeaders, arrayBuffer: ArrayBuffer, fileExtension: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200 || xhr.status === 204) {
            resolve(true);
          } else {
            reject(false);
          }
        }
      };

      xhr.open('POST', url, true);

      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-API-Version', '1');
      xhr.setRequestHeader('Authorization', headers.get('Authorization'));
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

      const file = new File([arrayBuffer], 'evidence' + fileExtension);
      const formData = new FormData();
      formData.append('EvidenceFile', file);

      xhr.send(formData);
    });
  }
  // Obsolete??
  public async getById(id: number): Promise<T> {
    let result: T = null;

    const request = this.dependencies.http.get(this.buildUrl(), { headers: this.buildHeaders() }).toPromise();

    await request
      .then((response: Response) => (result = this.parseSingleResponse(response)))
      .catch((error) => this.handleApiError(error, request));

    return Promise.resolve(result);
  }

  public async save(item: any): Promise<T[]> {
    let result: T[] = null;

    const request = this.dependencies.http.post<ApiResponseSummary<T>>(this.buildUrl(), this.normalizeRequestPayload(item), { headers: this.buildHeaders() }).toPromise();

    await request
      .then((response: ApiResponseSummary<T>) => (result = this.parseCollectionResponse(response)))
      .catch((error) => this.handleApiError(error, request));

    return Promise.resolve(result);
  }

public async update(item: any): Promise<ApiResponseSummary<T>> {
    let result: ApiResponseSummary<T>;

    const url     = this.buildUrl();
    const headers = this.buildHeaders();

    try {
      const payload: ApiResponseSummary<T> = await this.dependencies.http
        .put<ApiResponseSummary<T>>(url, this.normalizeRequestPayload(item), { headers })
        .toPromise();

      result = {
        success: true,
        message: '',
        data: this.parseCollectionResponse(payload)
      };
    }
    catch (error: any) {
      if (error.status === 409 || error.status === 507) {
        const errs = Array.isArray(error.error?.errors) ? error.error.errors : [];
        const msg  = errs[1] ?? 'Conflict – could not update item';
        result = {
          success: false,
          message: msg,
          data: [{ status: error.status }] as any
        };
      } else {
        this.handleApiError(error, null);
      }
    }

    return result;
  }

  public async command(data: any = null): Promise<T[]> {
    let result: T[] = null;

    const request = this.dependencies.http.post<ApiResponseSummary<T>>(this.buildUrl(), this.normalizeRequestPayload(data), { headers: this.buildHeaders() }).toPromise();

    await request
      .then((response: ApiResponseSummary<T>) => (result = this.parseCollectionResponse(response)))
      .catch((error) => this.handleApiError(error, request));

    return Promise.resolve(result);
  }

  public async delete(id: number): Promise<boolean> {
    let result = false;

    const request = this.dependencies.http.delete(this.buildUrl(), { headers: this.buildHeaders(), observe: 'response' }).toPromise();
    await request
      .then((response: HttpResponse<any>) => {
        result = response.status === 200;
      }) // TODO improve this check to be a standardized ServiceAction response type to check validity or errors
      .catch((error) => this.handleApiError(error, request));

    return Promise.resolve(result);
  }

  public async isOnline(): Promise<boolean> {
    let isOnline = false;
    await this.setEndpoint('/offline/testIsOnline')
      .getSingleNoThrow()
      .then((result) => {
        if (result) {
          isOnline = true;
        } else {
          isOnline = false;
        }
      })
      .catch((err) => {
        isOnline = false;
      });

    return Promise.resolve(isOnline);
  }

  private normalizeRequestPayload(payload: any): any {
    return this.normalizeRequestValue(payload);
  }

  private normalizeRequestValue(value: any): any {
    if (value instanceof Date) {
      return formatAsString(value, 'YYYY-MM-DDTHH:mm:ss');
    }

    if (this.isMomentLike(value)) {
      return formatAsString(value, 'YYYY-MM-DDTHH:mm:ss');
    }

    if (value instanceof FormData || value instanceof Blob || value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeRequestValue(item));
    }

    if (value !== null && typeof value === 'object') {
      const normalized: any = {};

      Object.keys(value).forEach((key) => {
        normalized[key] = this.normalizeRequestValue(value[key]);
      });

      return normalized;
    }

    return value;
  }

  private isMomentLike(value: any): boolean {
    return !!value && typeof value === 'object' && value._isAMomentObject === true;
  }

  private parseSingleResponse(response: Response): T {
    let result: T;

    if (!response) {
      return null;
    }

    if (response['success'] === true) {
      if (response.hasOwnProperty('data')) {
        result = response['data'] as T;
      }
    }
    this.resetEndpointFromCache();
    return result;
  }

  private parseCollectionResponse(response: ApiResponseSummary<T>): T[] {
    let result: T[] = [];
    if (!response) {
      return null;
    }

    if (response['success'] === true) {
      if (response.hasOwnProperty('data')) {
        result = response['data'] as T[];
      }
    }
    this.resetEndpointFromCache();
    return result;
  }

  private parseDataQueryCollectionResponse(response: Response): CollectionResponse<T> {
    if (!response) {
      return null;
    }
    const result = new CollectionResponse<T>(0, 0, 0, null);
    if (response['success'] === true) {
      result.items = [];
      if (response !== null && response.hasOwnProperty('data')) {
        if (response['data'] !== null && response['data']['items'] !== null) {
          result.items = response['data']['items'] as T[];
          result.itemsCount = response['data'].itemCount;
          result.pages = response['data'].pageCount;
          result.page = response['data'].pageNumber;
        }
      }
    }

    this.resetEndpointFromCache();
    return result;
  }
}
